// src/hooks/useList/useRequest.ts
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  UseQueryOptions,
} from "@tanstack/react-query";
import { Services, SERVICES_URL } from "$utils/typeConstants";
import { HttpCodes } from "$utils/HttpCodes";
import { ListParams, ListResponse } from "./types";
import { getFullUrl } from "./utils";
import useAuth from "$hooks/useAuth";

// Error types
export class ApiError extends Error {
  public statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  data?: any;
  headers?: Record<string, string>;
}

/**
 * Base API request function
 */
export const apiRequest = async <T>(
  url: string,
  service: Services = Services.MAIN_API,
  options: RequestOptions = {}
): Promise<T> => {
  const {
    method = "GET",
    data = undefined,
    headers: customHeaders = {},
  } = options;

  // Get base URL from service
  const baseUrl = SERVICES_URL[service];
  const fullUrl = `${baseUrl}/${url}`;

  // Set up headers
  const headers = new Headers({
    "Content-Type": "application/json",
    ...customHeaders,
  });

  // Get token from localStorage
  const token = localStorage.getItem("access_token") || (options as any).token;

  if (token) {
    headers.append("Authorization", `Bearer ${token}`);
  }

  // Make request
  const response = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  // Handle non-successful responses
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || "Unknown error";
    } catch {
      errorMessage = errorText || "Unknown error";
    }

    throw new ApiError(errorMessage, response.status);
  }

  // Parse JSON response
  const result = await response.json();
  return result;
};

/**
 * Hook for making data requests using React Query
 */
export function useRequest<T>(
  endpoint: string,
  params: ListParams,
  options: {
    service?: Services;
    additionalParams?: Record<string, any>;
    companySidebarFilter?: any;
    ignoreSideBarFilter?: boolean;
    cacheKey?: string;
    queryOptions?: Omit<
      UseQueryOptions<ListResponse<T>, ApiError>,
      "queryKey" | "queryFn"
    >;
  } = {}
) {
  const {
    service = Services.MAIN_API,
    additionalParams = {},
    companySidebarFilter,
    ignoreSideBarFilter = false,
    cacheKey,
    queryOptions = {},
  } = options;

  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const isConsolidatedEndpoint = endpoint.includes("consolidated");

  // Build query key that changes when params change
  const queryKey = cacheKey
    ? [
        cacheKey,
        params.pagination.pageIndex,
        params.pagination.pageSize,
        params.filters,
        params.sort,
        additionalParams,
      ]
    : [
        endpoint,
        params.pagination.pageIndex,
        params.pagination.pageSize,
        params.filters,
        params.sort,
        additionalParams,
      ];

  // Use React Query to manage request state
  const query = useQuery({
    queryKey,

    queryFn: async () => {
      const url = getFullUrl(
        endpoint,
        params,
        additionalParams,
        companySidebarFilter,
        ignoreSideBarFilter
      );

      try {
        return await apiRequest<ListResponse<T>>(url, service);
      } catch (error) {
        // Logout on unauthorized
        if (
          error instanceof ApiError &&
          error.statusCode === HttpCodes.UNAUTHORIZED
        ) {
          logout();
        }
        // Consolidated endpoints are used for list screens; when they are down (404/500),
        // we must not let React Query thrash the UI into repeated mounts/refetches.
        // Returning an empty result keeps the UI stable and prevents request loops.
        if (
          isConsolidatedEndpoint &&
          error instanceof ApiError &&
          (error.statusCode === HttpCodes.NOT_FOUND || error.statusCode >= 500)
        ) {
          return {
            data: [],
            meta: {
              pagination: {
                total: 0,
                pageCount: 0,
                page: params.pagination.pageIndex + 1,
                pageSize: params.pagination.pageSize,
              },
            },
          } as ListResponse<T>;
        }

        throw error;
      }
    },
    placeholderData: keepPreviousData,
    // No retries for consolidated (prevents hammering when endpoint is down).
    retry:
      (queryOptions as any)?.retry ??
      (isConsolidatedEndpoint ? false : undefined),
    ...queryOptions,
  });

  // Mutation for adding an item
  const addMutation = useMutation({
    mutationFn: (newItem: T) =>
      apiRequest(`${endpoint}`, service, {
        method: "POST",
        data: newItem,
      }),

    onSuccess: () => {
      // queryClient.invalidateQueries([endpoint]);
    },
  });

  // Mutation for updating an item
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<T> }) =>
      apiRequest(`${endpoint}/${id}`, service, {
        method: "PUT",
        data,
      }),

    onSuccess: () => {
      // queryClient.invalidateQueries([endpoint]);
    },
  });

  // Mutation for removing an item
  const removeMutation = useMutation({
    mutationFn: (id: string | number) =>
      apiRequest(`${endpoint}/${id}`, service, { method: "DELETE" }),

    onSuccess: () => {
      // queryClient.invalidateQueries([endpoint]);
    },
  });

  // Get the queryClient's setQueryData method for manual updates
  const setQueryData = (newData: ListResponse<T>) => {
    queryClient.setQueryData(queryKey, newData);
  };

  return {
    ...query,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    addItem: addMutation.mutate,
    updateItem: updateMutation.mutate,
    removeItem: removeMutation.mutate,
    setQueryData,
  };
}
